import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute, Link } from "@tanstack/react-router";

import { CtaBand, FaqList, PageHero, PeopleStrip } from "@/components/page-parts";
import { ExecutorIntakeForm } from "@/components/executor-intake-form";
import { FIRM, FIRM_ADDRESS, faqSchema } from "@/lib/firm";
import { PROBATE_LOCATIONS } from "@/lib/probate-locations";
import { ServiceReading } from "@/components/service-reading";

const PROBATE_FEES = {
  grant: "$3,300",
  admin: "$8,800",
} as const;

const FAQS = [
  {
    q: "How much does probate cost in Queensland?",
    a: `Our fixed professional fee for a straightforward grant of probate is ${PROBATE_FEES.grant} including GST. On top of that sit the disbursements, which are set by others and not by us — the Supreme Court filing fee and the cost of publishing the required notice. We tell you both figures in writing before we start, and the estate normally pays them.`,
  },
  {
    q: "What does the fixed fee cover?",
    a: "Reviewing the will and confirming it is valid on its face, identifying the executors and assets, preparing and publishing the notice of intention to apply, serving the Public Trustee, preparing the application, affidavit and supporting material, filing in the Supreme Court of Queensland and dealing with any requisitions from the registry until the grant issues.",
  },
  {
    q: "Do I actually need probate?",
    a: "Usually only where the estate holds real property in the deceased's sole name, or bank, share or investment holdings above the institution's threshold. Assets held as joint tenants pass automatically to the surviving owner, and superannuation and life insurance often sit outside the estate entirely. We will tell you honestly if you do not need a grant rather than sell you one.",
  },
  {
    q: "How long does probate take in Queensland?",
    a: "The notice of intention to apply must be published and served on the Public Trustee, and the application cannot be filed for at least 14 days after that. Where the paperwork is in order, a grant commonly issues within six to ten weeks of starting. Collecting the assets and distributing the estate takes longer again.",
  },
  {
    q: "What if there is no will?",
    a: "Then the application is for letters of administration rather than probate, and the person entitled to apply is determined by the intestacy rules. The process, the notices and our fixed fee are the same, but there is more work identifying and evidencing the entitled next of kin.",
  },
  {
    q: "What is the difference between probate and administering the estate?",
    a: `Probate is the court's confirmation that the will is valid and the executor may act — on its own that is the ${PROBATE_FEES.grant} fixed fee. Full estate administration is everything: obtaining the grant itself, then closing accounts, transferring or selling property, dealing with superannuation, insurers and the ATO, paying debts, preparing estate accounts and distributing to beneficiaries. That is one all-inclusive fixed fee of ${PROBATE_FEES.admin} including GST, covering up to 3 bank accounts, the retirement village exit or one property transfer, and distribution to up to 6 beneficiaries — more complex estates quoted.`,
  },
  {
    q: "Can I do it myself?",
    a: "You can. The registry issues requisitions for defective material, and executors are personally liable for getting distribution wrong — including distributing within six months of death while a family provision claim is still on foot. If the estate is simple and you have the time, self-filing is possible; most executors would rather not carry the risk.",
  },
  {
    q: "Do you charge a percentage of the estate?",
    a: "No. Some firms and trustee companies charge a percentage of the estate value, which means the fee rises with a beachfront house that took no extra work. We charge a fixed professional fee for the grant and a scoped fee for administration.",
  },
];

const STEPS = [
  {
    t: "1. We confirm a grant is actually needed",
    d: "A short conversation about what the deceased owned and how it was held. If the assets pass by survivorship or fall under the bank's threshold, you may not need probate at all.",
  },
  {
    t: "2. Notice of intention to apply",
    d: "We publish the required notice and serve the Public Trustee of Queensland. The application cannot be filed until at least 14 days later.",
  },
  {
    t: "3. Application prepared and filed",
    d: "The application, the executor's affidavit and the original will are prepared and filed in the Supreme Court of Queensland. We handle any registry requisitions.",
  },
  {
    t: "4. Grant issues",
    d: "Typically six to ten weeks from starting where the paperwork is clean. You then have the authority to deal with the estate's assets.",
  },
  {
    t: "5. Administration, if you want us to",
    d: "Collecting assets, transferring or selling property, superannuation and insurance claims, debts and tax, estate accounts and distribution — scoped and quoted separately.",
  },
];

export const Route = createFileRoute("/probate/")({
  head: () => ({
    meta: [
      { title: fitTitle("Probate Sunshine Coast | Estate Administration | Coastside Law") },
      {
        name: "description",
        content:
          fitDescription("Sunshine Coast probate lawyers in Golden Beach, Caloundra. Grants $3,300 incl GST plus costs. Deceased estate administration quoted to suit the estate."),
      },
      { property: "og:title", content: fitTitle(`Probate Sunshine Coast | ${FIRM.name}`) },
      {
        property: "og:description",
        content:
          fitDescription("Solicitor-led probate and deceased estate administration for Sunshine Coast executors from our Golden Beach office in Caloundra."),
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://coastsidelaw.com.au/probate" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "https://coastsidelaw.com.au/probate" }],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS)) }],
  }),
  component: ProbatePage,
});

function ProbatePage() {
  return (
    <>
      <PageHero
        personSlug="michael-klein"
        eyebrow="Sunshine Coast · Probate & estate administration"
        title="Probate lawyers on the Sunshine Coast"
        lead="Help for executors in Caloundra, Golden Beach and across the Sunshine Coast. We prepare Queensland probate applications and help administer deceased estates from our Golden Beach office. Tell us whether you need a grant, help after probate or the whole estate managed; we confirm the scope and fee in writing."
      />

      <section className="mx-auto max-w-3xl px-5 py-16">
        <h2 className="font-serif text-2xl text-foreground">Local help with a Sunshine Coast deceased estate</h2>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">
          Michael Klein assists executors from our office at {FIRM_ADDRESS}. You can discuss
          the estate by phone or video, including where beneficiaries live interstate. Tell us
          whether there is a will, who is named as executor and what assets the deceased held.
          A court grant and the later administration are different tasks: obtaining probate alone
          does not complete the work of collecting assets, addressing liabilities and distributing.
        </p>
        <p className="mt-4 mb-10 text-base leading-relaxed text-muted-foreground">
          <Link to="/probate-enquiry" className="font-semibold text-primary underline">Discuss a Sunshine Coast probate or estate administration enquiry</Link>,
          or read <Link to="/probate-queensland" className="font-semibold text-primary underline">when probate is required in Queensland</Link>.
        </p>
        <h2 className="font-serif text-2xl text-foreground">A probate lawyer or help with the whole deceased estate?</h2>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">
          The right scope depends on what you want to manage yourself. A probate application
          concerns the court grant; an estate administration lawyer can also help identify and
          collect estate assets, address debts and tax with the relevant advisers, keep accounts
          and arrange distribution. You may need administration advice even where no grant is
          required. We confirm which tasks you are instructing us to handle, rather than assume
          a grant-only quote covers everything.
        </p>
        <p className="mt-4 mb-10 text-base leading-relaxed text-muted-foreground">
          For a local appointment in Golden Beach, Caloundra or Pelican Waters, bring the will
          if available, a broad asset list and any correspondence from banks or the registry.
          Our <Link to="/guides/$slug" params={{ slug: "how-to-apply-for-probate-in-queensland" }} className="font-semibold text-primary underline">Queensland probate application guide</Link>
          {" "}explains the steps and common document questions. Avoid posting private estate
          information in public comments.
        </p>
        <div className="rounded-sm border border-border bg-card p-7">
          <h2 className="font-serif text-2xl text-foreground">What it costs</h2>
          <dl className="mt-5 space-y-4 text-sm leading-relaxed">
            <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-border/60 pb-4">
              <dt className="text-foreground">
                Grant of probate, or letters of administration
                <span className="block text-muted-foreground">
                  Fixed professional fee, including GST
                </span>
              </dt>
              <dd className="font-serif text-2xl text-primary">{PROBATE_FEES.grant}</dd>
            </div>
            <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-border/60 pb-4">
              <dt className="text-foreground">
                Full estate administration, including the grant of probate
                <span className="block text-muted-foreground">
                  All-inclusive fixed fee including GST — the grant itself, plus up to 3 bank
                  accounts, the retirement village exit or one property transfer, and distribution
                  to up to 6 beneficiaries. More complex estates quoted.
                </span>
              </dt>
              <dd className="font-serif text-2xl text-primary">{PROBATE_FEES.admin}</dd>
            </div>
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <dt className="text-foreground">
                Disbursements
                <span className="block text-muted-foreground">
                  Supreme Court filing fee and the published notice — set by others, charged at cost
                </span>
              </dt>
              <dd className="text-sm text-muted-foreground">At cost</dd>
            </div>
          </dl>
          <p className="mt-5 text-sm leading-relaxed text-muted-foreground">
            We do not charge a percentage of the estate. A larger estate does not make the
            application harder, so it should not make the fee bigger.
          </p>
        </div>

        <h2 className="mt-16 font-serif text-3xl text-foreground">How it runs</h2>
        <div className="mt-8 space-y-5">
          {STEPS.map((s) => (
            <div key={s.t} className="rounded-sm border-l-2 border-accent bg-secondary/40 p-6">
              <h3 className="font-serif text-xl text-foreground">{s.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.d}</p>
            </div>
          ))}
        </div>

        <div className="mt-14 rounded-sm border border-border bg-secondary/50 p-7">
          <h2 className="font-serif text-2xl text-foreground">Executors: two traps</h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            You are personally liable if you get it wrong. Do not distribute within six months of
            death — a family provision application can still be brought, and an executor who has
            already paid the money out can be left carrying it. And do not assume superannuation
            follows the will; it usually does not, and a lapsed or invalid binding nomination puts
            the decision back with the fund trustee.
          </p>
        </div>

        <PeopleStrip slugs={["michael-klein", "erin-wilkinson"]} heading="Who will handle it" />

        <h2 className="mt-16 font-serif text-3xl text-foreground">Probate by area</h2>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
          Executors deal with us from wherever they are — the application is filed electronically in
          the Supreme Court of Queensland. These pages cover what tends to come up in each area.
        </p>
        <ul className="mt-6 flex flex-wrap gap-2">
          {PROBATE_LOCATIONS.map((l) => (
            <li key={l.slug}>
              <Link
                to="/probate/$suburb"
                params={{ suburb: l.slug }}
                className="inline-block rounded-sm border border-border bg-card px-3 py-1.5 text-sm text-foreground transition-colors hover:border-primary"
              >
                Probate {l.name}
              </Link>
            </li>
          ))}
          <li>
            <Link
              to="/probate-solicitor-brisbane"
              className="inline-block rounded-sm border border-border bg-card px-3 py-1.5 text-sm text-foreground transition-colors hover:border-primary"
            >
              Probate Brisbane
            </Link>
          </li>
          <li>
            <Link
              to="/brisbane"
              className="inline-block rounded-sm border border-border bg-card px-3 py-1.5 text-sm text-foreground transition-colors hover:border-primary"
            >
              Brisbane hub
            </Link>
          </li>
          <li>
            <Link
              to="/remote-probate"
              className="inline-block rounded-sm border border-border bg-card px-3 py-1.5 text-sm text-foreground transition-colors hover:border-primary"
            >
              Interstate and overseas executors
            </Link>
          </li>
          <li>
            <Link
              to="/executor-queensland"
              className="inline-block rounded-sm border border-border bg-card px-3 py-1.5 text-sm text-foreground transition-colors hover:border-primary"
            >
              Executor duties in Queensland
            </Link>
          </li>
        </ul>


        <h2 className="mt-16 font-serif text-3xl text-foreground">Common questions</h2>
        <div className="mt-8">
          <FaqList items={FAQS} />
        </div>

        <p className="mt-10 text-sm text-muted-foreground">
          Read our guides on{" "}
          <Link
            to="/guides/$slug"
            params={{ slug: "do-i-need-probate-in-queensland" }}
            className="font-semibold text-primary underline"
          >
            whether you need probate
          </Link>
          ,{" "}
          <Link
            to="/guides/$slug"
            params={{ slug: "what-happens-if-you-die-without-a-will-in-queensland" }}
            className="font-semibold text-primary underline"
          >
            dying without a will in Queensland
          </Link>{" "}
          and{" "}
          <Link
            to="/guides/$slug"
            params={{ slug: "estate-contested-executor-guide-queensland" }}
            className="font-semibold text-primary underline"
          >
            what an executor does when the estate is contested
          </Link>
          .
        </p>

        <p className="mt-4 text-sm text-muted-foreground">
          Facing a claim against the estate? See{" "}
          <Link to="/estate-disputes" className="font-semibold text-primary underline">
            estate disputes
          </Link>
          . Planning ahead instead? See{" "}
          <Link to="/testamentary-trusts" className="font-semibold text-primary underline">
            testamentary trust wills
          </Link>
          .
        </p>

        <div className="mt-14">
          <ExecutorIntakeForm heading="Start a probate enquiry" />
        </div>
        <ServiceReading service="probate" />
      </section>

      <CtaBand
        title="Send us the will and we will tell you what is needed"
        body={`Call ${FIRM.phone} or send the will and a list of assets through. We will confirm whether a grant is required and give you the fee in writing.`}
      />
      <div className="h-20" />
    </>
  );
}
